import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { discardSurveyDraft } from '../Services/Surveys.service';
import type { Survey, SurveyDetail, SurveyListResponse } from '../Types/Surveys.types';
import { useActionToast } from '@/Hooks/useActionToast';

/**
 * Throw the open draft away (R41) — `POST /:id/discard-draft`, behind `survey:update`.
 *
 * **It is not a rejection, and the toast must not read like one.** `reject` is the verdict of
 * whoever reviewed and leaves `rejectedAt` + `rejectionReason` behind; this is the author binning
 * their own draft and writes no review stamp at all. The wording here is the last place that
 * distinction can be lost, after the button and the dialog.
 *
 * The `200` carries the whole survey, so the detail cache is written **from the response** rather
 * than refetched — and the caller reseeds the builder from that same payload, because the
 * questionnaire on screen belonged to the version that just went. The listing is invalidated
 * because the cards read `draftVersion` too.
 *
 * Nothing about the delivered version changes: not `version`, not `approvalStatus`, not
 * `reaskFromVersion`, not one response. So results, responses and comments are deliberately left
 * alone — invalidating them would suggest something moved there.
 */
export const useDiscardSurveyDraft = (surveyId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: () => discardSurveyDraft(surveyId),
    onSuccess: (survey: SurveyDetail) => {
      queryClient.setQueryData<Survey>(surveysQueryKeys.detail(surveyId), survey);
      queryClient.setQueriesData<SurveyListResponse>(
        { queryKey: surveysQueryKeys.lists() },
        current =>
          current && current.data.some(item => item.id === survey.id)
            ? {
                ...current,
                data: current.data.map(item => (item.id === survey.id ? survey : item)),
              }
            : current,
      );

      toast.success(
        'Rascunho descartado',
        `As edições foram jogadas fora. A versão ${survey.version} continua no ar exatamente como estava, e ninguém precisou revisar nada.`,
      );
    },
    onError: (error: Error) => {
      /*
        The `409` — there is no draft open — is the one worth reading whole: it says a draft that
        was just approved is no longer a draft but the delivered version, and points at
        `rollback-version`. `toast.failure` shows the BFF's own message, which is why the service
        does not overwrite it.
      */
      toast.failure('Não foi possível descartar o rascunho', error);
    },
  });
};
